import { useState } from "react";
import { Truck, Package, X, AlertTriangle } from "lucide-react";
import { api } from "@/api/client";

export default function ShipMethodDialog({ mailing, onSelectShippo, onClose, onFreightComplete }) {
  const [mode, setMode] = useState("choose"); // "choose" | "freight"
  const [freightForm, setFreightForm] = useState({ carrier: "", pro_number: "", cost: "" });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const override = mailing.shipping_override;

  const handleFreightSubmit = async () => {
    const newErrors = {};
    if (!freightForm.carrier.trim()) newErrors.carrier = "Carrier name is required";
    if (!freightForm.pro_number.trim()) newErrors.pro_number = "PRO number is required";

    // Number rather than parseFloat: parseFloat('12abc') is 12, and a cost is
    // going onto an invoice. The server rejects a negative cost, so it is
    // caught here where the message can point at the field.
    const cost = Number(freightForm.cost);
    if (!freightForm.cost.trim() || !Number.isFinite(cost) || cost < 0) {
      newErrors.cost = "Shipping cost must be an amount in dollars, 0 or more";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setSaving(true);
    try {
      /*
       * One shipment row covers the whole pallet, and the server builds it.
       *
       * This used to POST a hand-assembled row to /api/shipments, a route that
       * does not exist - so no freight shipment could be recorded at all, and
       * the only freight in the database arrived through the Base44 import. The
       * row it assembled was Base44-shaped too: mailing_ids, routes and a box
       * count in parcel_weight, a column that means pounds.
       *
       * Everything beyond these three typed fields is derived from the mailing
       * by the endpoint - the name, the combined route list, the container
       * count in pallet_box_count, and the destination from the shipping
       * override. It also records the FREIGHT_RECORDED activity entry.
       */
      await api.shipping.freight(mailing.id, {
        carrier: freightForm.carrier.trim(),
        pro_number: freightForm.pro_number.trim(),
        cost,
      });

      onFreightComplete();
    } catch (err) {
      console.error(err);
      // The server's own message is the useful one: an unpacked mailing says so
      // specifically, and a generic failure hid that.
      setErrors({ submit: err.message || "Failed to save freight shipment. Please try again." });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{
      position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
      background: "rgba(0,0,0,0.5)", display: "flex",
      alignItems: "center", justifyContent: "center", zIndex: 1100, padding: "1rem"
    }}>
      <div className="card" style={{ maxWidth: "480px", width: "100%" }}>
        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1.25rem" }}>
          <div>
            <h3 style={{ fontSize: "1.25rem", fontWeight: 700, color: "#1e293b" }}>
              {mode === "choose" ? "Select Shipping Method" : "Freight Shipment Details"}
            </h3>
            <p style={{ fontSize: "0.8rem", color: "#64748b", marginTop: "0.25rem" }}>{mailing.mailing_name}</p>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: "#64748b" }}>
            <X size={22} />
          </button>
        </div>

        {/* Override notice */}
        <div style={{
          padding: "0.625rem 0.875rem", background: "#fff7ed", border: "1px solid #fdba74",
          borderRadius: "8px", fontSize: "0.8rem", color: "#c2410c", fontWeight: 600, marginBottom: "1.25rem",
          display: "flex", alignItems: "center", gap: "0.5rem"
        }}>
          <AlertTriangle size={14} />
          Override Active — Shipping to: {override?.customer_name}, {override?.address_line1}, {override?.city}, {override?.state} {override?.zip}
        </div>

        {mode === "choose" && (
          <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
            <button
              onClick={onSelectShippo}
              style={{
                display: "flex", alignItems: "center", gap: "1rem",
                padding: "1.25rem", border: "2px solid #bfdbfe", borderRadius: "12px",
                background: "#eff6ff", cursor: "pointer", textAlign: "left", width: "100%"
              }}
            >
              <div style={{ width: "44px", height: "44px", borderRadius: "10px", background: "#3b82f6", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <Package size={22} color="white" />
              </div>
              <div>
                <div style={{ fontSize: "1rem", fontWeight: 700, color: "#1e40af" }}>Shippo</div>
                <div style={{ fontSize: "0.8rem", color: "#3b82f6" }}>Generate carrier labels via Shippo</div>
              </div>
            </button>

            <button
              onClick={() => setMode("freight")}
              style={{
                display: "flex", alignItems: "center", gap: "1rem",
                padding: "1.25rem", border: "2px solid #fed7aa", borderRadius: "12px",
                background: "#fff7ed", cursor: "pointer", textAlign: "left", width: "100%"
              }}
            >
              <div style={{ width: "44px", height: "44px", borderRadius: "10px", background: "#f97316", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <Truck size={22} color="white" />
              </div>
              <div>
                <div style={{ fontSize: "1rem", fontWeight: 700, color: "#c2410c" }}>Freight</div>
                <div style={{ fontSize: "0.8rem", color: "#f97316" }}>Enter freight carrier, PRO number & cost</div>
              </div>
            </button>
          </div>
        )}

        {mode === "freight" && (
          <div>
            {errors.submit && <div className="error" style={{ marginBottom: "1rem" }}>{errors.submit}</div>}

            <div style={{ display: "flex", flexDirection: "column", gap: "1rem", marginBottom: "1.5rem" }}>
              <div className="form-group">
                <label className="form-label">Carrier Name *</label>
                <input
                    className="form-input"
                    placeholder="e.g. XPO Logistics, Estes, Old Dominion"
                    value={freightForm.carrier}
                    onChange={e => { setFreightForm(prev => ({ ...prev, carrier: e.target.value })); setErrors(prev => ({ ...prev, carrier: "" })); }}
                    style={{ borderColor: errors.carrier ? "#dc2626" : undefined }}
                  />
                {errors.carrier && <span style={{ fontSize: "0.75rem", color: "#dc2626" }}>{errors.carrier}</span>}
              </div>

              <div className="form-group">
                <label className="form-label">PRO Number *</label>
                <input
                    className="form-input"
                    placeholder="Freight tracking / PRO number"
                    value={freightForm.pro_number}
                    onChange={e => { setFreightForm(prev => ({ ...prev, pro_number: e.target.value })); setErrors(prev => ({ ...prev, pro_number: "" })); }}
                    style={{ borderColor: errors.pro_number ? "#dc2626" : undefined }}
                  />
                {errors.pro_number && <span style={{ fontSize: "0.75rem", color: "#dc2626" }}>{errors.pro_number}</span>}
              </div>

              <div className="form-group">
                <label className="form-label">Shipping Cost ($) *</label>
                <input
                    className="form-input"
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="0.00"
                    value={freightForm.cost}
                    onChange={e => { setFreightForm(prev => ({ ...prev, cost: e.target.value })); setErrors(prev => ({ ...prev, cost: "" })); }}
                    style={{ borderColor: errors.cost ? "#dc2626" : undefined }}
                  />
                {errors.cost && <span style={{ fontSize: "0.75rem", color: "#dc2626" }}>{errors.cost}</span>}
              </div>
            </div>

            <div style={{ display: "flex", gap: "0.75rem", justifyContent: "flex-end" }}>
              <button className="btn btn-outline" onClick={() => setMode("choose")} disabled={saving}>
                Back
              </button>
              <button
                className="btn"
                onClick={handleFreightSubmit}
                disabled={saving}
                style={{
                  background: "linear-gradient(135deg, #f97316 0%, #ea580c 100%)",
                  color: "white", border: "none",
                  boxShadow: "0 4px 12px rgba(249,115,22,0.3)"
                }}
              >
                <Truck size={18} />
                {saving ? "Saving..." : "Freight Shipment Complete"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}